import React, { useState } from 'react';
import { Badge, Button } from '../common';
import { Heart, Clock, Eye } from 'iconoir-react';
import type { AnonymousProfile } from '../../services/discoverService';
import { useExpressInterest } from '../../hooks/useDiscover';
import toast from 'react-hot-toast';
import { Toaster } from '../common';
import { InfoTip } from '../common/InfoTip';
import { Tooltip } from '@radix-ui/themes';
import { TOOLTIPS } from '../../tooltips/copy';
import { cleanProfileSummary } from '../../utils';

interface AnonymousProfileCardProps {
  profile: AnonymousProfile;
  onViewProfile?: (profile: AnonymousProfile) => void;
}

const URGENCY_CONFIG: Record<string, { label: string; color: 'error' | 'warning' | 'info' | 'gray' }> = {
  urgent: { label: 'Urgent', color: 'error' },
  time_sensitive: { label: 'Time Sensitive', color: 'warning' },
  ongoing: { label: 'Ongoing', color: 'info' },
  exploratory: { label: 'Exploring', color: 'gray' },
};

/**
 * Parse the profile_summary into structured sections.
 * Summary format from backend: "Industry: FinTech · Experience: 10 years · Offers: ... · Looking for: ..."
 */
function parseSummary(summary: string) {
  const sections: Record<string, string> = {};
  if (!summary) return sections;

  // Split by " · " separator used by backend anonymizeSummary
  const parts = summary.split(' · ');
  for (const part of parts) {
    const colonIdx = part.indexOf(':');
    if (colonIdx > 0) {
      const key = part.substring(0, colonIdx).trim();
      const value = part.substring(colonIdx + 1).trim();
      if (value && value !== 'Not specified') {
        sections[key] = value;
      }
    }
  }
  return sections;
}

const AnonymousProfileCard: React.FC<AnonymousProfileCardProps> = ({
  profile,
  onViewProfile,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { mutate: expressInterest, isPending } = useExpressInterest();

  // Jun-16 (a user V1 test, O3): "your onboarding never asked any time related
  // questions, is that a slot?". Root cause (verified live): the AI service
  // hardcodes urgency='ongoing' on EVERY summary write (the column is NOT NULL
  // default 'ongoing'); onboarding never maps a timeline answer to urgency. So
  // 'ongoing' is a universal fabricated default carrying zero signal. Treat
  // 'ongoing' (and null) as "no urgency set" and render no badge; only a
  // genuinely-chosen urgent / time_sensitive / exploratory shows a badge.
  const urgencyConfig =
    profile.urgency && profile.urgency !== 'ongoing'
      ? URGENCY_CONFIG[profile.urgency]
      : undefined;
  const sections = parseSummary(profile.profile_summary);

  const handleExpressInterest = () => {
    expressInterest(
      { to_user_id: profile.id },
      {
        onSuccess: (response: any) => {
          const message = response.result?.match_created
            ? 'Mutual interest! A match has been created.'
            : 'Interest expressed! They will be notified.';
          toast.custom(t => (
            <Toaster message={message} type="success" id={t.id} />
          ));
        },
        onError: (error: any) => {
          toast.custom(t => (
            <Toaster
              message={error?.message || 'Failed to express interest'}
              type="error"
              id={t.id}
            />
          ));
        },
      }
    );
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
    }
    const months = Math.floor(diffDays / 30);
    return `${months} month${months === 1 ? '' : 's'} ago`;
  };

  // Industry is used only for the friendly fallback name (D8) when the backend
  // sends a raw "Member #<hex>" display name.
  const industry = sections['Industry'];

  // Jun-16 (a user V1 test, D8): the backend anonymiser falls back to
  // "Member #<hex-id>", which exposes a raw user id and reads as a cryptic
  // label. Until the backend sends a human pseudonym, render a clean fallback.
  const isRawMemberId = /^member\s*#/i.test(profile.display_name || '');
  const friendlyName = isRawMemberId
    ? industry
      ? `${industry} professional`
      : 'Verified member'
    : profile.display_name;

  return (
    <div className="bg-white rounded-xl border border-purple-25 p-4 hover:shadow-md transition-shadow flex flex-col">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-heading-500 mb-1 truncate">
            {friendlyName}
          </h3>
          <div className="flex items-center gap-2 text-xs text-body-400">
            <Clock className="w-3 h-3 flex-shrink-0" />
            <span>Active {getTimeAgo(profile.last_active_at)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Apr-25: urgency badge wrapped with explainer tooltip.
              Users were seeing "Ongoing" / "Urgent" / "Exploring" pills
              with no idea what the levels mean.
              Jun-16 (a user V1 test, O3): only render when the user actually
              set a timeline — no fabricated "Ongoing" default. */}
          {urgencyConfig && (
            <Tooltip content={TOOLTIPS['discover-urgency'].body}>
              <span className="inline-flex cursor-help">
                <Badge size="xs" color={urgencyConfig.color} className="whitespace-nowrap">
                  {urgencyConfig.label}
                </Badge>
              </span>
            </Tooltip>
          )}
          {profile.compatibility_hint && (
            <span className="text-xs font-semibold text-primary-500 whitespace-nowrap">
              {profile.compatibility_hint}%
            </span>
          )}
        </div>
      </div>

      {/* Profile summary. Jun-18 (a user V1 test, D5): every card now shows the
          same anonymized summary as its body, instead of conditional
          Offers/Seeks/Achievement/Skills rows that rendered only when the
          persona captured those sections, making the grid ragged (some cards
          rich, some bare). The full structured detail lives in the profile
          detail modal. */}
      <div className="space-y-2 mb-3 flex-1">
        {profile.profile_summary && (
          <p className={`text-sm text-body-600 ${isExpanded ? '' : 'line-clamp-4'}`}>
            {cleanProfileSummary(profile.profile_summary)}
          </p>
        )}
      </div>

      {/* Expand/collapse when the summary is long enough to be clamped */}
      {profile.profile_summary && profile.profile_summary.length > 180 && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs text-primary-500 hover:text-primary-600 mb-3 self-start"
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}

      {/* Objectives tags */}
      {profile.objectives?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {profile.objectives.slice(0, 3).map((obj, idx) => (
            <span
              key={idx}
              className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full"
            >
              {obj}
            </span>
          ))}
          {profile.objectives.length > 3 && (
            <span className="px-2 py-0.5 text-gray-400 text-xs">
              +{profile.objectives.length - 3} more
            </span>
          )}
        </div>
      )}

      {/* Actions */}
      <div data-tour-id="discover-card" className="flex gap-2 items-center">
        <Button
          data-tour-id="discover-interest"
          size="sm"
          className="flex-1"
          onClick={handleExpressInterest}
          isLoading={isPending}
        >
          <Heart className="w-4 h-4" />
          Express Interest
        </Button>
        <InfoTip tooltipKey="express-interest" side="top" />
        {onViewProfile && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onViewProfile(profile)}
            title="View full profile"
          >
            <Eye className="w-4 h-4" />
            View
          </Button>
        )}
      </div>
    </div>
  );
};

export default AnonymousProfileCard;
